import { customLog } from 'helpers/customLog';

import React, { createContext, useCallback, useEffect, useRef } from 'react';

import { useAuth0 } from '@auth0/auth0-react';

import { sentryService } from '@/services/index';

import {
  AnalyticsContextValue,
  AnalyticsProviderProps,
  ContentProperties,
  EcommerceBaseProperties,
  FormProperties,
  PageProperties,
  Product,
  SignedInProperties,
} from './types';

declare const window: any;

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

const normalizePackageType = (packageType?: string): string | undefined => {
  if (!packageType) return undefined;

  const normalizedValue = packageType.trim().toLowerCase();

  if (
    normalizedValue === 'broadband' ||
    normalizedValue === 'broadband-services' ||
    normalizedValue === 'broadband ssn' ||
    normalizedValue === 'broadband-one-off-fee' ||
    normalizedValue === 'broadband-technician-fee' ||
    normalizedValue === 'broadband-devices'
  ) {
    return 'Broadband';
  }

  if (normalizedValue === 'tv' || normalizedValue === 'dth') {
    return 'DTH';
  }

  if (
    ['tv-broadband', 'tv + broadband', 'dth & broadband', 'dth + broadband bundle'].includes(
      normalizedValue
    )
  ) {
    return 'DTH + Broadband Bundle';
  }

  return packageType;
};

const normalizeTrackingProperties = <T extends Record<string, any> | undefined>(
  properties: T
): T => {
  if (!properties || typeof properties !== 'object') {
    return properties;
  }

  if (!Object.prototype.hasOwnProperty.call(properties, 'package_type')) {
    return properties;
  }

  return {
    ...properties,
    package_type: normalizePackageType(properties.package_type),
  };
};

const allowedVariants = new Set([
  'package-upgrade',
  'one-off-fee',
  'recording',
  'box',
  'monthly-fee',
  'hindi-channel',
  'broadband',
  'specialist-channel',
  'broadband-devices',
  'broadband-one-off-fee',
  'broadband-services',
  'broadband-technician-fee',
  'packages',
  'voice',
  'voice-addons',
  'voice-crosscountry',
  'additional-charges',
]);

const variantAliases: Record<string, string> = {
  package: 'packages',
  packages: 'packages',
  'package-upgrade': 'package-upgrade',
  'one off fee': 'one-off-fee',
  'monthly fee': 'monthly-fee',
  'hindi channel': 'hindi-channel',
  'specialist channel': 'specialist-channel',
  'broadband devices': 'broadband-devices',
  'broadband device': 'broadband-devices',
  'broadband one off fee': 'broadband-one-off-fee',
  'broadband services': 'broadband-services',
  'broadband service': 'broadband-services',
  'broadband technician fee': 'broadband-technician-fee',
  'voice addons': 'voice-addons',
  'voice add ons': 'voice-addons',
  'voice crosscountry': 'voice-crosscountry',
  'additional charges': 'additional-charges',
};

const normalizeVariant = (variant?: string): string | undefined => {
  if (!variant) return undefined;

  const normalizedValue = variant.trim().toLowerCase();
  if (allowedVariants.has(normalizedValue)) {
    return normalizedValue;
  }

  const hyphenatedValue = normalizedValue.replace(/[_\s]+/g, '-');
  if (allowedVariants.has(hyphenatedValue)) {
    return hyphenatedValue;
  }

  return variantAliases[normalizedValue] ?? variantAliases[hyphenatedValue] ?? variant;
};

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
  defaultPageProperties,
  debug = false,
}) => {
  const { isAuthenticated, user, isLoading } = useAuth0();
  const pagePropertiesRef = useRef<Partial<PageProperties>>(defaultPageProperties);

  // Get user_id from Auth0
  const userId = isAuthenticated && !isLoading ? user?.sub : undefined;

  useEffect(() => {
    pagePropertiesRef.current = defaultPageProperties;
  }, [defaultPageProperties]);

  const debugLog = useCallback(
    (event: string, properties: any) => {
      if (debug) {
        customLog('[Analytics]', event, properties);
      }
    },
    [debug]
  );

  const safeAnalyticsCall = useCallback(
    (
      method: 'page' | 'track',
      eventOrProperties: any,
      properties?: any,
      onTracked?: () => void
    ) => {
      try {
        if (window.analytics && typeof window.analytics[method] === 'function') {
          if (method === 'page') {
            debugLog('page', eventOrProperties);
            // Call page with explicit context including path, url, title, referrer
            const pageContext = {
              path: window.location.pathname,
              url: window.location.href,
              title: document.title,
              search: window.location.search,
            };
            // analytics.page([category], [name], [properties], [options])
            // We pass undefined for category and name, then properties with context
            window.analytics.page(undefined, undefined, eventOrProperties, {
              context: { page: pageContext },
            });
          } else {
            const normalizedProperties = normalizeTrackingProperties(properties);
            // Automatically inject user_id into all track events
            const propsWithUserId = { ...normalizedProperties, userId: userId };
            debugLog(eventOrProperties, propsWithUserId);
            window.analytics.track(eventOrProperties, propsWithUserId, undefined, onTracked);
          }
        } else {
          sentryService.captureMessage('[Analytics] Segment not loaded', 'warning');
          onTracked?.();
        }
      } catch (error) {
        sentryService.captureException(error);
        onTracked?.();
      }
    },
    [debugLog, userId]
  );

  const trackPage = useCallback(
    (properties: Partial<PageProperties>) => {
      const mergedProps = { ...pagePropertiesRef.current, ...properties };
      safeAnalyticsCall('page', mergedProps);
    },
    [safeAnalyticsCall]
  );

  const trackSignedIn = useCallback(
    (properties: Partial<SignedInProperties>) => {
      const mergedProps = { ...properties };
      safeAnalyticsCall('track', 'Signed In', mergedProps);
    },
    [safeAnalyticsCall]
  );

  const trackProductListViewed = useCallback(
    (products: Product[], listName: string, extraProps?: EcommerceBaseProperties) => {
      const packageType = normalizePackageType(extraProps?.package_type);

      safeAnalyticsCall('track', 'Product List Viewed', {
        ...extraProps,
        package_type: packageType,
        list_name: listName,
        products,
      });
    },
    [safeAnalyticsCall]
  );

  const trackProductClicked = useCallback(
    (
      product: Product,
      listName: string,
      linkText: string,
      linkUrl: string,
      extraProps?: EcommerceBaseProperties
    ) => {
      safeAnalyticsCall('track', 'Product Clicked', {
        ...extraProps,
        list_name: listName,
        link_text: linkText,
        link_url: linkUrl,
        products: [product],
      });
    },
    [safeAnalyticsCall]
  );

  const trackProductViewed = useCallback(
    (product: Product, extraProps?: EcommerceBaseProperties) => {
      safeAnalyticsCall('track', 'Product Viewed', { ...extraProps, products: [product] });
    },
    [safeAnalyticsCall]
  );

  const trackProductAdded = useCallback(
    (product: Product, context: string, extraProps?: EcommerceBaseProperties) => {
      const packageType = normalizePackageType(extraProps?.package_type);
      const variant = normalizeVariant(product.variant);

      safeAnalyticsCall('track', 'Product Added', {
        ...extraProps,
        package_type: packageType,
        context,
        category: product.category,
        variant,
        brand: product.brand,
        name: product.name,
        price: product.price,
        original_price: product.original_price,
        product_discount: product.product_discount,
        product_coupon: product.product_coupon,
        quantity: product.quantity,
        id: product.id,
      });
    },
    [safeAnalyticsCall]
  );

  const trackProductRemoved = useCallback(
    (product: Product, extraProps?: EcommerceBaseProperties) => {
      safeAnalyticsCall('track', 'Product Removed', {
        ...extraProps,
        category: product.category,
        variant: product.variant,
        brand: product.brand,
        name: product.name,
        price: product.price,
        original_price: product.original_price,
        product_discount: product.product_discount,
        quantity: product.quantity,
        id: product.id,
        product_coupon: null,
      });
    },
    [safeAnalyticsCall]
  );

  const trackCartViewed = useCallback(
    (products: Product[], extraProps?: EcommerceBaseProperties) => {
      safeAnalyticsCall('track', 'Cart Viewed', { ...extraProps, products });
    },
    [safeAnalyticsCall]
  );

  const trackCheckoutStarted = useCallback(
    (products: Product[], extraProps?: EcommerceBaseProperties) => {
      safeAnalyticsCall('track', 'Checkout Started', { ...extraProps, products });
    },
    [safeAnalyticsCall]
  );

  const trackCheckoutStepCompleted = useCallback(
    (step: number, stepName: string, products: Product[], extraProps?: EcommerceBaseProperties) => {
      safeAnalyticsCall('track', 'Checkout Step Completed', {
        ...extraProps,
        step,
        step_name: stepName,
        products,
      });
    },
    [safeAnalyticsCall]
  );

  const trackPaymentInfoEntered = useCallback(
    (paymentMethod: string, products: Product[], extraProps?: EcommerceBaseProperties) => {
      safeAnalyticsCall('track', 'Payment Info Entered', {
        ...extraProps,
        payment_method: paymentMethod,
        products,
      });
    },
    [safeAnalyticsCall]
  );

  const trackOrderCompleted = useCallback(
    (orderId: string, products: Product[], extraProps?: EcommerceBaseProperties) => {
      safeAnalyticsCall('track', 'Order Completed', { ...extraProps, order_id: orderId, products });
    },
    [safeAnalyticsCall]
  );

  const trackCtaClicked = useCallback(
    (properties: ContentProperties, onTracked?: () => void) => {
      safeAnalyticsCall('track', 'Cta Clicked', properties, onTracked);
    },
    [safeAnalyticsCall]
  );

  const trackCardClicked = useCallback(
    (properties: ContentProperties) => {
      safeAnalyticsCall('track', 'Card Clicked', properties);
    },
    [safeAnalyticsCall]
  );

  const trackPopupViewed = useCallback(
    (properties: ContentProperties) => {
      safeAnalyticsCall('track', 'Popup Viewed', properties);
    },
    [safeAnalyticsCall]
  );

  const trackPopupClosed = useCallback(
    (properties: ContentProperties) => {
      safeAnalyticsCall('track', 'Popup Closed', properties);
    },
    [safeAnalyticsCall]
  );

  const trackFlipsideViewed = useCallback(
    (properties: ContentProperties) => {
      safeAnalyticsCall('track', 'Flipside Viewed', properties);
    },
    [safeAnalyticsCall]
  );

  const trackContentExpanded = useCallback(
    (properties: ContentProperties) => {
      safeAnalyticsCall('track', 'Content Expanded', properties);
    },
    [safeAnalyticsCall]
  );

  const trackCarouselNavigated = useCallback(
    (properties: ContentProperties) => {
      safeAnalyticsCall('track', 'Carousel Navigated', properties);
    },
    [safeAnalyticsCall]
  );

  const trackErrorViewed = useCallback(
    (properties: FormProperties) => {
      safeAnalyticsCall('track', 'Error Viewed', properties);
    },
    [safeAnalyticsCall]
  );

  const trackFormStarted = useCallback(
    (formName: string) => {
      safeAnalyticsCall('track', 'Form Started', { form_name: formName });
    },
    [safeAnalyticsCall]
  );

  const trackFormStepViewed = useCallback(
    (formName: string, formStep: string) => {
      safeAnalyticsCall('track', 'Form Step Viewed', { form_name: formName, form_step: formStep });
    },
    [safeAnalyticsCall]
  );

  const trackFormFieldUpdated = useCallback(
    (properties: FormProperties) => {
      safeAnalyticsCall('track', 'Form Field Updated', properties);
    },
    [safeAnalyticsCall]
  );

  const trackFormSubmitted = useCallback(
    (formName: string) => {
      safeAnalyticsCall('track', 'Form Submitted', { form_name: formName });
    },
    [safeAnalyticsCall]
  );

  const trackFormCompleted = useCallback(
    (formName: string) => {
      safeAnalyticsCall('track', 'Form Completed', { form_name: formName });
    },
    [safeAnalyticsCall]
  );

  const trackFormAbandoned = useCallback(
    (formName: string) => {
      safeAnalyticsCall('track', 'Form Abandoned', { form_name: formName });
    },
    [safeAnalyticsCall]
  );
  const trackBroadbandAvailabilityChecked = useCallback(
    (availabilityStatus: string) => {
      safeAnalyticsCall('track', 'Broadband Availability Checked', {
        availability_status: availabilityStatus,
      });
    },
    [safeAnalyticsCall]
  );

  const trackVoucherCoupon = useCallback(
    (products: Product[], trackEventName: string, extraProps?: EcommerceBaseProperties) => {
      safeAnalyticsCall('track', trackEventName, {
        ...extraProps,
        ...(products?.length ? { products } : {}),
      });
    },
    [safeAnalyticsCall]
  );

  const value: AnalyticsContextValue = {
    trackPage,
    trackSignedIn,
    trackProductListViewed,
    trackProductClicked,
    trackProductViewed,
    trackProductAdded,
    trackProductRemoved,
    trackCartViewed,
    trackCheckoutStarted,
    trackCheckoutStepCompleted,
    trackPaymentInfoEntered,
    trackOrderCompleted,
    trackCtaClicked,
    trackCardClicked,
    trackPopupViewed,
    trackPopupClosed,
    trackFlipsideViewed,
    trackContentExpanded,
    trackCarouselNavigated,
    trackErrorViewed,
    trackFormStarted,
    trackFormStepViewed,
    trackFormFieldUpdated,
    trackFormSubmitted,
    trackFormCompleted,
    trackFormAbandoned,
    trackBroadbandAvailabilityChecked,
    trackVoucherCoupon,
  };

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};

export default AnalyticsContext;
